import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

interface ProgressVelocityChartProps {
  data: Array<{
    month: string;
    velocity: number;
    target_velocity: number;
    workshops_completed: number;
  }>;
  loading?: boolean;
}

const ProgressVelocityChart: React.FC<ProgressVelocityChartProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Progress Velocity Trends</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Progress Velocity Trends</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No velocity data available
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Progress Velocity Trends</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="month" 
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              domain={[0, 'dataMax + 10']}
              tickFormatter={(value) => `${value}%`}
            />
            <Tooltip 
              formatter={(value: number, name: string) => [
                `${value.toFixed(1)}%`, 
                name === 'velocity' ? 'Actual Velocity' : 'Target Velocity'
              ]}
              labelFormatter={(label) => `Month: ${label}`}
            />
            <Line 
              type="monotone" 
              dataKey="target_velocity" 
              stroke="#e5e7eb" 
              strokeWidth={2}
              strokeDasharray="5 5"
              name="target_velocity"
            />
            <Line 
              type="monotone" 
              dataKey="velocity" 
              stroke="#3b82f6" 
              strokeWidth={3}
              name="velocity"
              dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 flex items-center justify-center space-x-6 text-sm">
        <div className="flex items-center">
          <div className="w-4 h-0.5 bg-blue-500 mr-2"></div>
          <span>Actual Velocity</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-0.5 bg-gray-300 border-dashed border-t-2 mr-2"></div>
          <span>Target Velocity</span>
        </div>
      </div>
    </div>
  );
};

export default ProgressVelocityChart;
